import { useGenerateCommunityPrompt } from '@communities/prompt-management/hooks';
import { transformErrors } from '@core/adapters/errors';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { ErrorDisplay } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Dialog, LoadingOverlay } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import * as styles from './RegeneratePromptDialog.css';

interface RegeneratePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
}

/**
 * RegeneratePromptDialog Component
 *
 * Displays a confirmation dialog for regenerating AI prompts
 */
function RegeneratePromptDialogComponent({ isOpen, onClose, communityId }: RegeneratePromptDialogProps) {
  // Simple state management like DeletePromptDialog
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewPrompt, setIsNewPrompt] = useState<boolean | null>(null);

  // Use dedicated hook for generating prompts
  const { generatePrompts, errors: hookErrors, loading: _isGeneratingPrompt } = useGenerateCommunityPrompt();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsGenerating(false);
      setShowSuccess(false);
      setError(null);
    }
  }, [isOpen]);

  // Handle success state with timer
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (!isGenerating && showSuccess) {
      // Delay closing to show success message
      timer = setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000); // 3 second delay to ensure state is fully updated
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isGenerating, showSuccess, onClose]);

  const handleRegenerate = async () => {
    try {
      // Set local loading state
      setIsGenerating(true);
      setError(null);

      // Generate the prompt using Apollo hook
      const result = await generatePrompts(communityId, {
        labelFeedback: [], // Empty array for basic regeneration
      });

      // Capture whether this is a new prompt
      setIsNewPrompt(result.isNew ?? null);

      // If we get here, the operation was successful
      setShowSuccess(true);
    } catch {
      setError('Failed to regenerate prompt');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCancel = () => onClose();
  const handleCloseError = () => onClose();

  // Show loading overlay during generation or success
  if (isGenerating || showSuccess) {
    const getSuccessMessage = () => {
      if (isGenerating) return 'Generating safety prompts...';

      if (isNewPrompt === true) {
        return 'Safety prompt generation completed. A new prompt version has been created and will be displayed shortly.';
      } else {
        return 'Safety prompt generation completed. No changes detected, using existing cached prompts.';
      }
    };

    return <LoadingOverlay fullScreen message={getSuccessMessage()} spinnerSize="large" />;
  }

  // Handle error state with dialog
  const hasError = !!(error || (hookErrors && hookErrors.length > 0));
  const domainErrors = hasError ? (error ? transformErrors(error) : hookErrors) : null;

  const getErrorDisplayProps = () => {
    if (!domainErrors || domainErrors.length === 0) return null;

    const firstError = domainErrors[0];

    return {
      title: 'Error',
      message: formatErrorMessages(domainErrors),
      correlationId:
        firstError.details && typeof firstError.details === 'object' && 'correlationId' in firstError.details
          ? (firstError.details.correlationId as string)
          : undefined,
      onRetry: handleRegenerate,
      onBack: handleCancel,
    };
  };

  const errorProps = getErrorDisplayProps();

  return (
    <>
      <Dialog
        cancelAction={{
          label: 'Cancel',
          disabled: false,
        }}
        description="Generate safety prompts when automatic generation fails or no prompt exists for this community."
        isOpen={isOpen && !hasError}
        primaryAction={{
          label: 'Generate',
          onClick: handleRegenerate,
          variant: 'ai',
        }}
        size="md"
        title="Generate Safety Prompts"
        onClose={onClose}
      >
        <div className={styles.confirmationMessage}>
          <p>
            Prompts are automatically generated when label definitions or community metadata (description, PG rating, genres, guidelines) change. Use manual
            generation if automatic generation failed or to create a new version for testing.
          </p>
        </div>
      </Dialog>

      {/* Error Dialog */}
      {errorProps && <ErrorDisplay isOpen={hasError} size="md" onClose={handleCloseError} {...errorProps} />}
    </>
  );
}

export const RegeneratePromptDialog = withAppInsightsTracking(RegeneratePromptDialogComponent, 'RegeneratePromptDialog');
